/**
 * @file Minimal fetch wrapper with timeout + typed error handling.
 * Every service goes through this so retry/telemetry can be added in one place.
 */

import { REQUEST_TIMEOUT_MS } from '../config/scrWeeklyConfig';

/** Error carrying HTTP status + the URL that failed, for friendly UI messages. */
export class HttpError extends Error {
  /**
   * @param {string} message
   * @param {Object} [opts]
   * @param {number} [opts.status]
   * @param {string} [opts.url]
   */
  constructor(message, { status, url } = {}) {
    super(message);
    this.name = 'HttpError';
    this.status = typeof status === 'number' ? status : 0;
    this.url = url || '';
  }
}

/**
 * Fetch and parse JSON with an abort-based timeout.
 * @template T
 * @param {string} url
 * @param {RequestInit} [init]
 * @returns {Promise<T>}
 */
export async function getJson(url, init = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(url, {
      ...init,
      signal: controller.signal,
      headers: { Accept: 'application/json', ...(init.headers || {}) },
    });
  } catch (err) {
    clearTimeout(timer);
    const aborted = err && err.name === 'AbortError';
    throw new HttpError(
      aborted ? 'Request timed out.' : 'Network request failed.',
      { url }
    );
  }
  clearTimeout(timer);

  if (!response.ok) {
    throw new HttpError(`Request failed (${response.status}).`, {
      status: response.status,
      url,
    });
  }

  try {
    return /** @type {T} */ (await response.json());
  } catch (err) {
    throw new HttpError('Response was not valid JSON.', { url });
  }
}
